import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TeamLogo } from "./TeamLogo";
import { api } from "../lib/api";
import { Switch } from "./ui/switch";

const f1 = (n) => Number(n).toFixed(1);
const HOME_BOOST = 0.03;

const withBoost = (row, on) => {
  if (!on) return row;
  const a = row.away.projection * (1 - HOME_BOOST);
  const h = row.home.projection * (1 + HOME_BOOST);
  const aPct = a + h ? Math.round((a / (a + h)) * 100) : 50;
  return { ...row, away: { ...row.away, projection: a, pct: aPct }, home: { ...row.home, projection: h, pct: 100 - aPct } };
};

const PropRow = ({ row, awayTeam, homeTeam }) => {
  const awayLeads = row.away.projection > row.home.projection;
  const homeLeads = row.home.projection > row.away.projection;
  return (
    <div data-testid={`team-prop-${row.key}`} className="py-2.5">
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span data-testid={`team-prop-${row.key}-away`} className={`font-data font-bold ${awayLeads ? "text-white" : "text-slate-400"}`}>
          {f1(row.away.projection)} <span className="text-slate-500 font-normal">({row.away.pct}%)</span>
        </span>
        <span className="text-slate-300 font-semibold uppercase tracking-wide text-[11px]">{row.label}</span>
        <span data-testid={`team-prop-${row.key}-home`} className={`font-data font-bold ${homeLeads ? "text-white" : "text-slate-400"}`}>
          <span className="text-slate-500 font-normal">({row.home.pct}%)</span> {f1(row.home.projection)}
        </span>
      </div>
      <div className="flex h-2.5 rounded-full overflow-hidden bg-[#233248] gap-0.5">
        <motion.div animate={{ width: `${row.away.pct}%` }} transition={{ duration: 0.9, ease: "easeOut" }}
          className="h-full" style={{ background: awayTeam.color || "#00E5FF", opacity: awayLeads ? 1 : 0.55 }} />
        <motion.div animate={{ width: `${row.home.pct}%` }} transition={{ duration: 0.9, ease: "easeOut" }}
          className="h-full" style={{ background: homeTeam.color || "#F59E0B", opacity: homeLeads ? 1 : 0.55 }} />
      </div>
      <div className="flex justify-between font-data text-[10px] text-slate-500 mt-1 whitespace-nowrap">
        <span>O {f1(row.away.offense_avg)} / D {f1(row.away.opp_defense_allowed)}</span>
        <span>O {f1(row.home.offense_avg)} / D {f1(row.home.opp_defense_allowed)}</span>
      </div>
    </div>
  );
};

export const TeamPropProjections = ({ matchupId, homeTeam, awayTeam }) => {
  const [data, setData] = useState(null);
  const [boost, setBoost] = useState(false);

  useEffect(() => {
    setData(null);
    api.get(`/matchups/${matchupId}/team-props`).then((r) => setData(r.data)).catch(() => setData({ available: false }));
  }, [matchupId]);

  if (!data?.available) return null;
  return (
    <div data-testid="team-prop-projections" className="mt-5 pt-4 border-t border-[#233248]">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <TeamLogo sport={awayTeam.sport} abbr={awayTeam.abbr} color={awayTeam.color} size="sm" />
          <span className="text-[11px] text-slate-500 font-data">AWAY</span>
        </div>
        <h4 className="font-heading font-bold text-white text-sm">Team Prop Projections</h4>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500 font-data">HOME</span>
          <TeamLogo sport={homeTeam.sport} abbr={homeTeam.abbr} color={homeTeam.color} size="sm" />
        </div>
      </div>
      <label className="flex items-center justify-end gap-2 mt-2 mb-1 cursor-pointer">
        <span className="text-[11px] text-slate-400 font-data">Home-field boost (+3% {homeTeam.abbr})</span>
        <Switch data-testid="home-field-toggle" checked={boost} onCheckedChange={setBoost} />
      </label>
      {data.props.map((row) => (
        <PropRow key={row.key} row={withBoost(row, boost)} awayTeam={awayTeam} homeTeam={homeTeam} />
      ))}
      <p data-testid="team-prop-note" className="text-[10px] text-slate-500 font-data mt-1">
        O = team offense per game · D = what the opponent's defense allows · projection = (O + D) ÷ 2 · {data.season} season averages (ESPN) · {boost ? `home-field boost on: ${homeTeam.abbr} +3%, ${awayTeam.abbr} −3%` : "no home-field boost"}
      </p>
    </div>
  );
};
